import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import LogService from '../services/LogService';
import { ChartDataResponse } from '../types/LogTypes';
import './Charts.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Charts: React.FC = () => {
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadChartData();
  }, []);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await LogService.getChartData();
      setChartData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="charts-loading">Loading charts...</div>;
  }

  if (error) {
    return (
      <div className="charts-error">
        <p>Error: {error}</p>
        <button onClick={loadChartData}>Retry</button>
      </div>
    );
  }

  if (!chartData) {
    return <div className="charts-error">No chart data available</div>;
  }

  // Prepare data for Errors by Hour chart
  const errorsByHourData = {
    labels: chartData.errorsByHour.map(d => {
      const date = new Date(d.hour);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        hour12: false 
      });
    }),
    datasets: [
      {
        label: 'Error Count',
        data: chartData.errorsByHour.map(d => d.errorCount),
        backgroundColor: 'rgba(220, 53, 69, 0.6)',
        borderColor: 'rgba(220, 53, 69, 1)',
        borderWidth: 1,
      },
    ],
  };

  const errorsByHourOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Errors Over Time (Last 24 Hours)',
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Errors: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: 'Number of Errors',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time (Hour)',
        },
      },
    },
  };

  // Prepare data for Errors by Customer by Day chart
  // Get all unique customers and dates from the data
  const customers = Array.from(new Set(chartData.errorsByCustomerDay.map(d => d.customer))).sort();
  const allDates = Array.from(new Set(chartData.errorsByCustomerDay.map(d => d.date))).sort();

  const colors = [
    { bg: 'rgba(102, 126, 234, 0.6)', border: 'rgba(102, 126, 234, 1)' },
    { bg: 'rgba(255, 159, 64, 0.6)', border: 'rgba(255, 159, 64, 1)' },
    { bg: 'rgba(75, 192, 192, 0.6)', border: 'rgba(75, 192, 192, 1)' },
    { bg: 'rgba(153, 102, 255, 0.6)', border: 'rgba(153, 102, 255, 1)' },
    { bg: 'rgba(255, 99, 132, 0.6)', border: 'rgba(255, 99, 132, 1)' },
  ];

  const errorsByCustomerDayData = {
    labels: allDates.map(d => {
      const date = new Date(d);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: customers.map((customer, index) => ({
      label: customer,
      data: allDates.map(date => {
        const entry = chartData.errorsByCustomerDay.find(
          d => d.customer === customer && d.date === date
        );
        return entry ? entry.errorCount : 0;
      }),
      backgroundColor: colors[index % colors.length].bg,
      borderColor: colors[index % colors.length].border,
      borderWidth: 2,
      tension: 0.3,
    })),
  };

  const errorsByCustomerDayOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Errors by Customer Over Time (Last 7 Days)',
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y} errors`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: 'Number of Errors',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Date',
        },
      },
    },
  };

  return (
    <div className="charts">
      <h1>Error Analytics</h1>

      <div className="chart-container">
        <div className="chart-wrapper">
          <Bar data={errorsByHourData} options={errorsByHourOptions} />
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-wrapper">
          <Line data={errorsByCustomerDayData} options={errorsByCustomerDayOptions} />
        </div>
      </div>

      <div className="refresh-section">
        <button onClick={loadChartData} className="refresh-button">
          Refresh Charts
        </button>
      </div>
    </div>
  );
};

export default Charts;
